import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, Cog, RefreshCw, Layers, CheckCircle2, Clock, X } from 'lucide-react';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid } from '@/components/data';
import { SplitBadge } from '@/components/BatchSplitIndicator';


const STAGE_COLORS_MAP = {
  green: '#16a34a',
  blue: '#2563eb',
  gray: '#6b7280',
  orange: '#ea580c',
};

export default function SupervisorJobCardsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const initialTab = searchParams.get('tab') || 'all';
  const initialStage = searchParams.get('stage') || '';
  const initialSearch = searchParams.get('search') || '';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState(initialSearch);
  const [stageFilter, setStageFilter] = useState(initialStage);
  const [jobCards, setJobCards] = useState([]);
  const [stats, setStats] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [stages, setStages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchDebounce, setSearchDebounce] = useState(initialSearch);
  const limit = 30;

  // Fetch stages list
  useEffect(() => {
    api.get('/supervisor/stages').then(res => {
      setStages(res.data.stages || []);
    }).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch job cards
  const fetchJobCards = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('tab', activeTab);
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      if (searchDebounce) params.set('search', searchDebounce);
      if (stageFilter) params.set('stage', stageFilter);

      const res = await api.get(`/supervisor/job-cards?${params.toString()}`);
      setJobCards(res.data.job_cards || []);
      setStats(res.data.stats || {});
      setStageCounts(res.data.stage_counts || {});
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load job cards');
    }
    setLoading(false);
  }, [activeTab, page, searchDebounce, stageFilter]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, searchDebounce, stageFilter]);

  useEffect(() => {
    fetchJobCards();
  }, [fetchJobCards]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'all') params.set('tab', activeTab);
    if (stageFilter) params.set('stage', stageFilter);
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [activeTab, stageFilter, search, setSearchParams]);

  const totalPages = Math.ceil(total / limit);

  const tabs = [
    { key: 'all', label: 'All', count: stats.total, icon: Layers },
    { key: 'active', label: 'Active', count: stats.active, icon: Clock },
    { key: 'pending_assignment', label: 'Pending Machine', count: stats.pending_assignment, icon: Cog },
    { key: 'done', label: 'Completed', count: stats.done, icon: CheckCircle2 },
  ];

  const columns = [
    {
      key: 'item',
      header: 'Item / Customer',
      width: 'minmax(13rem,1.5fr)',
      render: (card) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {card.item_name || card.product_subtype || 'Item'}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {card.customer_name}{card.firm_name ? ` — ${card.firm_name}` : ''}
          </span>
        </>
      ),
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '9.5rem',
      mobile: false,
      render: (card) => <span className="block truncate text-[11px] text-slate-400">{card.order_id}</span>,
    },
    {
      key: 'current_stage_name',
      header: 'Stage',
      width: '10rem',
      render: (card) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: STAGE_COLORS_MAP[card.stage_color] || STAGE_COLORS_MAP.blue }} />
          <span className="truncate text-[12px] font-medium text-slate-700">{card.current_stage_name || '—'}</span>
          <SplitBadge batch={card} />
        </span>
      ),
    },
    {
      key: 'machine_name',
      header: 'Machine',
      width: '9rem',
      render: (card) => card.machine_name ? (
        <span className="flex min-w-0 items-center gap-1 text-[12px] text-slate-600">
          <Cog className="h-3 w-3 shrink-0 text-slate-400" />
          <span className="truncate">{card.machine_name}</span>
        </span>
      ) : (
        <span className="text-[11px] font-medium text-amber-600">Unassigned</span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (card) => <span className="font-medium">{card.quantity?.toLocaleString() || '—'}</span>,
      total: (rows) => rows.reduce((s, c) => s + (c.quantity || 0), 0).toLocaleString(),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      mobile: false,
      render: (card) => (
        <span className={`text-[11px] font-medium ${
          card.priority === 'high' ? 'text-red-600'
            : card.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }`}>{card.priority || '—'}</span>
      ),
    },
    {
      key: 'delivery_date',
      header: 'Delivery',
      width: '6.5rem',
      mobile: false,
      render: (card) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {card.delivery_date ? formatIST(card.delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'progress',
      header: 'Progress',
      width: '8rem',
      mobile: false,
      render: (card) => {
        const done = card.completed_stages || 0;
        const totalStages = card.total_stages || 0;
        const pct = totalStages ? Math.round((done / totalStages) * 100) : 0;
        return (
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-200">
              <span className="block h-full rounded-full bg-brand-blue" style={{ width: `${pct}%` }} />
            </span>
            <span className="text-[10px] text-slate-500">{done}/{totalStages}</span>
          </span>
        );
      },
    },
  ];

  return (
    <Screen data-testid="supervisor-job-cards-page">
      <CommandBar
        title="Job Cards"
        subtitle={`${total} card${total === 1 ? '' : 's'}`}
        actions={[{ key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchJobCards, disabled: loading }]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-11 pl-8 text-base lg:h-7 lg:text-xs"
            placeholder="Item, customer, order…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={tabs.map(t => ({
          key: t.key,
          label: t.label,
          value: t.count ?? 0,
          tone: t.key === 'pending_assignment' && t.count ? 'warn' : t.key === 'done' ? 'good' : 'default',
        }))}
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || 'all')}
      >
        {stageFilter && (
          <button
            type="button"
            onClick={() => setStageFilter('')}
            className="flex items-center gap-1 rounded bg-brand-blue/10 px-2 py-1 text-[11px] font-medium text-brand-blue"
          >
            {stageFilter} <X className="h-3 w-3" />
          </button>
        )}
      </StatStrip>

      <ScreenSplit
        railWidth="14rem"
        rail={
          <Pane title="Stages" aside={`${stages.length}`} className="max-h-full">
            <button
              type="button"
              onClick={() => setStageFilter('')}
              aria-pressed={!stageFilter}
              className={`flex w-full items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-[13px] transition-colors ${
                !stageFilter ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span className="truncate">All stages</span>
              <span className="shrink-0 text-[10px] text-slate-400">{stats.total ?? 0}</span>
            </button>
            {stages.map((stage) => {
              const name = stage.stage_name || stage.name || stage;
              const on = stageFilter === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setStageFilter(on ? '' : name)}
                  aria-pressed={on}
                  className={`flex w-full items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-[13px] transition-colors last:border-b-0 ${
                    on ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className="truncate">{name}</span>
                  <span className="shrink-0 text-[10px] text-slate-400">{stageCounts[name] ?? 0}</span>
                </button>
              );
            })}
          </Pane>
        }
      >
        <Pane
          title="Job cards"
          aside={stageFilter || 'all stages'}
          scroll={false}
          bodyClassName="flex flex-col"
          actions={totalPages > 1 && (
            <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
              <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
              {page} / {totalPages}
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
            </span>
          )}
        >
          <DataGrid
            data-testid="supervisor-job-cards-table"
            columns={columns}
            rows={jobCards}
            loading={loading}
            getRowId={(card) => card.row_key}
            onRowClick={(card) => navigate(`/supervisor/job-card/${card.line_item_id}`)}
            showTotals={jobCards.length > 0}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <Layers className="h-8 w-8 text-slate-300" />
                No job cards match these filters
              </span>
            }
          />
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
